<template>
    <div class="header-main">
        <div class="header-content">
            <div class="solutions-container">
                <slot name="top-nav" />
                <div class="video-wrap">
                    <div class="video-inner">
                        <video
                            class="video"
                            playsinline
                            autoplay
                            muted
                            preload
                            loop
                            :poster="video.poster"
                        >
                            <source
                                v-for="(item, index) in video.sources"
                                :key="`video-source-${index}`"
                                :src="item.link"
                                :type="`video/${item.type}`"
                            />
                        </video>
                        <button v-modal="video.ytId" class="play-button">
                            <a-tooltip
                                :content="video.tooltip"
                                placement="bottom"
                                :open-delay="1000"
                                popper-class="play-button-tooltip"
                            >
                                <div class="play-button-inner">
                                    <svg
                                        class="play-button-icon"
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="23"
                                        height="28"
                                        viewBox="0 0 23 28"
                                    >
                                        <path
                                            d="M22.5 13.134C23.1667 13.5189 23.1667 14.4811 22.5 14.866L1.5 26.9904C0.833331 27.3753
                                        -1.263e-06 26.8942 -1.22935e-06 26.1244L-1.6941e-07 1.87564C-1.35761e-07 1.10584 0.833334 0.624718
                                            1.5 1.00962L22.5 13.134Z"
                                        />
                                    </svg>
                                </div>
                            </a-tooltip>
                        </button>
                    </div>
                </div>
                <div class="header-inner">
                    <div class="content">
                        <div class="label-wrap">
                            <a-label v-if="label" v-bind="label">
                                {{ label.text }}
                            </a-label>
                        </div>

                        <h1 class="title">
                            {{ title }}
                        </h1>

                        <div class="lead">
                            {{ lead }}
                        </div>

                        <div class="body-text">
                            {{ bodyText }}
                        </div>

                        <div class="main-buttons">
                            <a-button
                                v-for="button in ctaButtons"
                                :key="button.link"
                                class="button"
                                v-bind="button"
                                :to="button.link"
                                :event="{ category: 'Button click', action: button.text, label: 'top menu' }"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <a-modal
            :id="video.ytId"
            is-youtube
            is-overlay-close
            type="youtube"
        >
            <a-youtube :yt-id="video.ytId" :params="video.ytParams || { autoplay: 1 }" />
        </a-modal>
    </div>
</template>

<script>
import AButton from '@core/components/button/button.vue';
import ALabel from '@core/components/label/label.vue';
import AModal from '@core/components/modal/modal.vue';
import ATooltip from '@core/components/tooltip/tooltip.vue';
import AYoutube from '@core/components/youtube/youtube.vue';
import Modal from '@core/directives/modal.js';

export default {
    name: 'SolutionsHeaderMain',

    components: {
        AButton,
        ALabel,
        ATooltip,
        AModal,
        AYoutube,
    },

    directives: { Modal },

    props: {
        label: {
            type: Object,
            default: null,
        },
        title: {
            type: String,
            required: true,
        },
        lead: {
            type: String,
            default: '',
        },
        bodyText: {
            type: String,
            default: '',
        },
        ctaButtons: {
            type: Array,
            default: () => [],
        },
        video: {
            type: Object,
            required: true,
        },
    },
};
</script>

<style lang="postcss" scoped>
.header-main {
    color: var(--av-inversed-primary);
    background: var(--av-nav-primary);
}

.header-content {
    position: relative;

    &::before {
        display: block;
        content: '';
        position: absolute;
        z-index: 1;
        height: 197px;
        bottom: 235px;
        width: 100%;
        background: linear-gradient(180deg, #00204d 0%, rgba(0, 32, 77, 0.00) 100%);

        @media (--viewport-mobile-wide) {
            height: 271px;
            bottom: 222px;
        }

        @media (--viewport-tablet) {
            bottom: auto;
            top: 0;
            height: 177px;
            background: linear-gradient(180deg, rgba(0, 32, 77, 0.50) 0%, rgba(0, 32, 77, 0.00) 100%);
        }
    }

    &::after {
        display: block;
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        height: 100%;
        width: 100%;
        background: var(--av-nav-primary);
        opacity: 0.2;
        z-index: 1;
    }

    @media (--viewport-desktop) {
        overflow: hidden;
    }
}

.header-inner {
    position: relative;
    border-top: 1px solid var(--av-inversed-lighter);
    z-index: 2;
}

.content {
    padding: 48px 0;
    position: relative;
    display: flex;
    flex-direction: column;
    min-height: 458px;

    @media (--viewport-tablet) {
        @mixin colls 7;
    }

    @media (--viewport-desktop) {
        @mixin colls 6;
    }
}

.label-wrap {
    margin-bottom: 16px;
}

.title {
    @mixin display-accent;
    margin-bottom: 16px;

    @media (--viewport-mobile-wide) {
        @mixin hero-accent;
    }

    @media (--viewport-desktop-wide) {
        @mixin large-accent;
    }
}

.lead {
    @mixin title;
    margin-bottom: 16px;
}

.body-text {
    @mixin paragraph;
    margin-bottom: 40px;

    @media (--viewport-desktop) {
        margin-bottom: 56px;
    }
}

.solutions-container {
    padding-bottom: 244px;

    @media (--viewport-mobile-wide) {
        padding-bottom: 302px;
    }

    @media (--viewport-tablet) {
        padding-bottom: 0;
    }
}

.video-wrap {
    @media (max-width: 1023px) {
        position: absolute;
        inset: 0;
        overflow: hidden;
    }
}

.video-inner {
    position: absolute;
    left: 0;
    right: 0;
    height: 516px;
    top: auto;
    bottom: -84px;

    @media (--viewport-mobile-wide) {
        bottom: -104px;
        height: 597px;
    }

    @media (--viewport-tablet) {
        height: auto;
        top: 0;
        bottom: 0;

        &::after {
            display: block;
            content: '';
            position: absolute;
            z-index: 1;
            top: 0;
            inset-inline-start: 0;
            height: 100%;
            width: 732px;
            background: linear-gradient(90deg, #00204d 10%, rgba(0, 32, 77, 0.00) 100%);
        }
    }

    @media (--viewport-desktop-large) {
        position: static;
    }
}

.video {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    height: 100%;

    @media (--viewport-desktop) {
        left: 0;
        right: 0;
        transform: none;
        height: 150%;
    }

    @media (--viewport-desktop-large) {
        height: 180%;
    }

    @media all and (min-width: 1921px) {
        height: 230%;
    }

    @media all and (min-width: 2561px) {
        height: 260%;
    }
}

.play-button {
    position: absolute;
    z-index: 5;
    height: 72px;
    width: 72px;
    border-radius: 40px;
    outline: 9px solid var(--av-inversed-lighter);
    background: var(--av-inversed-primary);
    top: 50%;
    margin-top: -16px;
    inset-inline-start: 50%;
    margin-inline-start: -36px;
    cursor: pointer;

    @media (--viewport-tablet) {
        inset-inline-start: 75%;
        margin-inline-start: 0;
    }
    @media (--viewport-desktop) {
        margin-inline-start: -36px;
    }

    svg {
        margin-left: 4px;
        fill: var(--av-button-main);
    }

    &:hover {
        outline: 9px solid var(--av-inversed-light);

        svg {
            fill: var(--el-primary-hover);
        }
    }
}

.a-tooltip {
    margin-top: -9px;
    margin-inline-start: -9px;
}

.play-button-inner {
    width: 90px;
    height: 90px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.main-buttons {
    margin-top: auto;
    display: flex;
    flex-direction: column;
    gap: 16px;

    @media (--viewport-desktop) {
        display: block;
    }
}

.button {
    min-width: 214px;

    @media (--viewport-desktop) {
        &:not(:last-child) {
            margin-inline-end: 16px;
        }
    }
}
</style>
<style lang="postcss">
.a-popper.play-button-tooltip {
    border-radius: 4px;
}
</style>
